/**
 * Scan findings.
 *
 * Each finding shows four things, in the order an administrator needs them:
 * what was checked, what was found, why it matters, and what to do. The
 * "found" and "what to do" text comes from the scanner; "checked" and "why it
 * matters" are explanations written here, keyed by the scanner's stable
 * finding key, so nobody has to already know what DMARC is.
 *
 * SEVERITY
 * The scanner bands findings RED / AMBER / GREEN. RED and AMBER map onto the
 * reserved status colours. GREEN is split in two on purpose: a GREEN finding
 * that carries advice ("Referrer-Policy not set") or merely records an absence
 * ("no IPv6") is informational, and shown neutral. Painting it "good" would
 * tell a reader something was verified that was not.
 */

import { useState, useMemo } from 'react';
import { Card, Pill, type Tone } from '../ui';
import { IconChevron } from '../icons';
import { cn } from '../../lib/cn';
import { useScanT, type TopicId, type ScanKey } from './strings';

export interface Finding {
  key: string;
  band: 'RED' | 'AMBER' | 'GREEN';
  title: string;
  detail: string;
  remediation?: string;
  category: string;
}

/** GREEN findings that record an absence rather than a verified good setting. */
const INFORMATIONAL_KEYS = new Set(['DNS_NO_IPV6', 'EMAIL_NO_MX']);

export type Severity = 'critical' | 'warning' | 'info' | 'ok';

export function severityOf(f: Finding): Severity {
  if (f.band === 'RED') return 'critical';
  if (f.band === 'AMBER') return 'warning';
  if (f.remediation || INFORMATIONAL_KEYS.has(f.key)) return 'info';
  return 'ok';
}

const SEVERITY_RANK: Record<Severity, number> = { critical: 0, warning: 1, info: 2, ok: 3 };

const SEVERITY_TONE: Record<Severity, Tone> = {
  critical: 'critical', warning: 'warning', info: 'neutral', ok: 'good',
};

const SEVERITY_LABEL: Record<Severity, ScanKey> = {
  critical: 'scans.band.critical',
  warning: 'scans.band.warning',
  info: 'scans.band.info',
  ok: 'scans.band.ok',
};

/** Which explanation a finding key belongs to. Header "OK" keys share their topic. */
export function topicFor(key: string): TopicId {
  const k = key.replace(/_OK$/, '');
  switch (k) {
    case 'DNS_NO_A_RECORD': case 'DNS_RESOLVES': return 'dnsResolve';
    case 'DNS_IPV6': case 'DNS_NO_IPV6': return 'dnsIpv6';
    case 'DNS_SINGLE_NAMESERVER': case 'DNS_MULTIPLE_NAMESERVERS': return 'dnsNs';
    case 'EMAIL_NO_SPF': case 'EMAIL_SPF_PERMISSIVE': case 'EMAIL_SPF_PRESENT': return 'spf';
    case 'EMAIL_NO_DMARC': case 'EMAIL_DMARC_ENFORCED':
    case 'EMAIL_DMARC_QUARANTINE': case 'EMAIL_DMARC_MONITOR_ONLY': return 'dmarc';
    case 'EMAIL_MX_PRESENT': case 'EMAIL_NO_MX': return 'mx';
    case 'TLS_UNAVAILABLE': return 'tlsReach';
    case 'TLS_MODERN_PROTOCOL': case 'TLS_OBSOLETE_PROTOCOL': return 'tlsProtocol';
    case 'TLS_CERT_EXPIRED': case 'TLS_CERT_EXPIRING': case 'TLS_CERT_VALID': return 'tlsCert';
    case 'TLS_CERT_UNTRUSTED': return 'tlsTrust';
    case 'HTTP_UNREACHABLE': return 'httpReach';
    case 'HTTP_NO_HSTS': return 'hsts';
    case 'HTTP_NO_CSP': return 'csp';
    case 'HTTP_NO_XCTO': return 'xcto';
    case 'HTTP_NO_XFO': return 'xfo';
    case 'HTTP_NO_REFERRER_POLICY': return 'referrer';
    case 'HTTP_SERVER_VERSION_DISCLOSED': return 'serverBanner';
    default: return 'unknown';
  }
}

const HEADER_MISSING = /^HTTP_NO_(HSTS|CSP|XCTO|XFO|REFERRER_POLICY)$/;

const AREA_ORDER = ['DNS', 'Email', 'TLS', 'HTTP headers'];
const KNOWN_AREAS = new Set(AREA_ORDER);

const areaKey = (category: string) => (KNOWN_AREAS.has(category) ? category : 'other');

// ---------------------------------------------------------------------------

function FindingRow({ finding, open, onToggle, vendorName }: {
  finding: Finding;
  open: boolean;
  onToggle: () => void;
  vendorName: string | null;
}) {
  const tt = useScanT();
  const severity = severityOf(finding);
  const topic = topicFor(finding.key);
  const area = areaKey(finding.category);
  const panelId = `finding-${finding.key}`;
  const title = tt(`scans.finding.${finding.key}` as ScanKey, { defaultValue: finding.title });
  const actionable = severity === 'critical' || severity === 'warning';

  return (
    <li className="border-t border-line first:border-t-0">
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        aria-controls={panelId}
        className="w-full flex items-start gap-3 py-3 text-left min-h-[44px]"
      >
        <IconChevron
          className={cn('w-4 h-4 mt-1 shrink-0 text-ink-muted transition-transform', open && 'rotate-90')}
          aria-hidden
        />
        <span className="min-w-0 flex-1">
          <span className="block text-sm font-medium text-ink break-words">{title}</span>
        </span>
        <Pill tone={SEVERITY_TONE[severity]} className="shrink-0">{tt(SEVERITY_LABEL[severity])}</Pill>
      </button>

      {open && (
        <div id={panelId} className="pl-7 pb-4 space-y-3 text-sm">
          <div>
            <h4 className="text-xs font-medium uppercase tracking-wide text-ink-secondary">
              {tt('scans.finding.checked')}
            </h4>
            <p className="mt-0.5 text-ink">
              {tt(`scans.topic.${topic}.checked` as ScanKey, { area: finding.category })}
            </p>
          </div>
          <div>
            <h4 className="text-xs font-medium uppercase tracking-wide text-ink-secondary">
              {tt('scans.finding.found')}
            </h4>
            {/* Scanner output, shown verbatim: it is the evidence. */}
            <p className="mt-0.5 text-ink break-words">
              {/* For an absent header the scanner's "detail" is an explanation,
                  not an observation; say what was observed, and leave the
                  explanation to "Why it matters". */}
              {HEADER_MISSING.test(finding.key) ? tt('scans.finding.headerMissing') : finding.detail}
            </p>
          </div>
          <div>
            <h4 className="text-xs font-medium uppercase tracking-wide text-ink-secondary">
              {tt('scans.finding.why')}
            </h4>
            <p className="mt-0.5 text-ink">{tt(`scans.topic.${topic}.why` as ScanKey)}</p>
          </div>
          <div>
            <h4 className="text-xs font-medium uppercase tracking-wide text-ink-secondary">
              {tt('scans.finding.fix')}
            </h4>
            <p className="mt-0.5 text-ink break-words">
              {finding.remediation ?? tt('scans.finding.noFix')}
            </p>
            {actionable && (
              <p className="mt-1 text-xs text-ink-secondary">
                {vendorName
                  ? tt('scans.detail.vendorOwner', { vendor: vendorName })
                  : `${tt('scans.finding.whoToAsk')}: ${tt(`scans.owner.${area}` as ScanKey)}`}
              </p>
            )}
          </div>
        </div>
      )}
    </li>
  );
}

/** Findings grouped by area, most severe first, issues open and the rest folded. */
export function FindingsList({ findings, vendorName }: {
  findings: Finding[];
  vendorName: string | null;
}) {
  const tt = useScanT();

  const groups = useMemo(() => {
    const byArea = new Map<string, Finding[]>();
    for (const f of findings) {
      const k = areaKey(f.category);
      byArea.set(k, [...(byArea.get(k) ?? []), f]);
    }
    const order = [...AREA_ORDER, 'other'].filter((k) => byArea.has(k));
    const worst = (items: Finding[]) => Math.min(...items.map((f) => SEVERITY_RANK[severityOf(f)]));
    return order
      .map((k) => ({
        area: k,
        items: [...byArea.get(k)!].sort(
          (a, b) => SEVERITY_RANK[severityOf(a)] - SEVERITY_RANK[severityOf(b)],
        ),
      }))
      // Areas with the most serious findings first, so the page opens on what
      // needs doing. Sort is stable, so ties keep the natural area order.
      .sort((a, b) => worst(a.items) - worst(b.items));
  }, [findings]);

  // Issues start open; informational and OK findings start folded.
  const defaultOpen = useMemo(
    () => new Set(findings.filter((f) => ['critical', 'warning'].includes(severityOf(f))).map((f) => f.key)),
    [findings],
  );
  const [openKeys, setOpenKeys] = useState<Set<string>>(defaultOpen);

  const toggle = (key: string) =>
    setOpenKeys((prev) => {
      const next = new Set(prev);
      if (!next.delete(key)) next.add(key);
      return next;
    });

  const allOpen = findings.length > 0 && findings.every((f) => openKeys.has(f.key));

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <button
          type="button"
          className="text-sm font-medium text-brand hover:underline min-h-[44px] px-1"
          onClick={() => setOpenKeys(allOpen ? new Set() : new Set(findings.map((f) => f.key)))}
        >
          {allOpen ? tt('scans.detail.collapseAll') : tt('scans.detail.expandAll')}
        </button>
      </div>

      {groups.map((g) => {
        const issues = g.items.filter((f) => ['critical', 'warning'].includes(severityOf(f))).length;
        return (
          <Card key={g.area}>
            <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1 mb-1">
              <h3 className="oc-h2 text-base">{tt(`scans.area.${g.area}` as ScanKey)}</h3>
              <span className={cn('text-xs font-medium', issues > 0 ? 'text-ink' : 'text-ink-secondary')}>
                {issues > 0
                  ? tt('scans.detail.areaIssues', { count: issues })
                  : tt('scans.detail.areaClear')}
              </span>
            </div>
            <ul>
              {g.items.map((f, i) => (
                <FindingRow
                  key={`${f.key}-${i}`}
                  finding={f}
                  open={openKeys.has(f.key)}
                  onToggle={() => toggle(f.key)}
                  vendorName={vendorName}
                />
              ))}
            </ul>
          </Card>
        );
      })}
    </div>
  );
}

/** Counts used by the summary tiles, taken from the findings actually shown. */
export function countBySeverity(findings: Finding[]) {
  const counts = { critical: 0, warning: 0, low: 0 };
  for (const f of findings) {
    const s = severityOf(f);
    if (s === 'critical') counts.critical++;
    else if (s === 'warning') counts.warning++;
    else counts.low++;
  }
  return counts;
}
